import { ChatbotUiState } from "@/components/enums/UiState";
import { BoundingBox, FaceDetector, FilesetResolver } from "@mediapipe/tasks-vision";
import store from "stores/store";

const test = (canvas: HTMLCanvasElement) => {
    document.body.appendChild(canvas);
    canvas.style.position = "absolute";
    canvas.style.zIndex = "10000";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.width = 256;
    canvas.height = 256;
    canvas.style.width = "256px";

    setTimeout(() => {
        document.body.removeChild(canvas);
    }, 5000);
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, errorMessage: string): Promise<T> => {
    let timeoutId: NodeJS.Timeout | undefined;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((_, reject) => {
                timeoutId = setTimeout(() => reject(new Error(errorMessage)), timeoutMs);
            }),
        ]);
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
    }
};

export const drawImageToCanvas = async (
    img: HTMLImageElement,
    isOnline: boolean,
): Promise<HTMLCanvasElement | null> => {
    if (!store) {
        throw Error("Component was not setup correctly");
    }
    const { updateImageProcessingState } = store;
    console.log("img.width: ", img.width);
    console.log("img.height: ", img.height);
    // const squareImage = await createSquareImage(img);
    // console.log("squareImage.width: ", squareImage.width);
    // console.log("squareImage.height: ", squareImage.height);
    console.log("Starting face detection");
    const largestFace = await withTimeout(detectLargestFace(img), 10000, "Face detection timed out.");
    console.log("Face detection completed");
    //const largestFace = await detectLargestFace(squareImage);
    if (!largestFace) {
        updateImageProcessingState({
            ...store.imageProcessingState,
            noFaceFound: true,
        });
        throw new Error("No face found in the image.");
    }

    const { originX, originY, width, height } = largestFace;
    console.log("originX: ", originX);
    console.log("originY: ", originY);
    console.log("width: ", width);
    console.log("height: ", height);
    /*
        const scale = Math.min(256 / width, 256 / height); 
        console.log("scale: ", scale);
    
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        console.log("scaledWidth: ", scaledWidth);
        console.log("scaledHeight: ", scaledHeight);
    
        const faceCenterX = (originX + width / 2) * scale;
        const faceCenterY = (originY + height / 2) * scale;
        console.log("faceCenterX: ", faceCenterX);
        console.log("faceCenterY: ", faceCenterY);
    
        // Center Horizontally
        const offsetX = 512 - faceCenterX;
        // Center Vertically, but ensure it fits within the 1024px height if not bottom alligned
        const offsetY = scaledHeight < 1024 ? 1024 - scaledHeight : 512 - faceCenterY;
    */
    const smallerSide = Math.min(img.width, img.height);
    const scaledWidth = 1024 * (img.width / smallerSide);
    const scaledHeight = 1024 * (img.height / smallerSide);
    const offsetX = (1024 - scaledWidth) / 2;
    const offsetY = (1024 - scaledHeight) / 2;

    console.log("smallerSide: ", smallerSide);
    console.log("scaledWidth: ", scaledWidth);
    console.log("scaledHeight: ", scaledHeight);
    console.log("offsetX: ", offsetX);
    console.log("offsetY: ", offsetY);

    const drawCanvas: HTMLCanvasElement = document.createElement("canvas");
    drawCanvas.width = isOnline ? 1024 : 2160;
    drawCanvas.height = isOnline ? 1024 : 2160;



    const drawContext: CanvasRenderingContext2D | null = drawCanvas.getContext("2d");

    if (drawContext) {
        drawContext.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
        if (isOnline) {
            drawContext.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);
        } else {
            // Handle rotation for vertical camera
            // drawContext.translate(drawCanvas.width / 2, drawCanvas.height / 2);
            // drawContext.rotate((270 * Math.PI) / 180);
            // drawContext.scale(scaledWidth, scaledHeight);
            // drawContext.drawImage(img, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        }
    }
    return drawCanvas;
};

const createSquareImage = (img: HTMLImageElement): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const targetSize = Math.max(img.width, img.height);
        console.log("Target size for square image: ", targetSize);

        const squareCanvas: HTMLCanvasElement = document.createElement("canvas");
        const squareContext: CanvasRenderingContext2D | null = squareCanvas.getContext("2d");

        squareCanvas.width = targetSize;
        squareCanvas.height = targetSize;

        const offsetX = (targetSize - img.width) / 2;
        const offsetY = (targetSize - img.height) / 2;

        squareContext?.clearRect(0, 0, targetSize, targetSize);
        squareContext?.drawImage(img, offsetX, offsetY, img.width, img.height);

        const squareImage = new Image();
        squareImage.onload = () => {
            resolve(squareImage);
        };

        squareImage.onerror = (error) => {
            reject(new Error("Failed to create square image."));
        };
        squareImage.src = squareCanvas.toDataURL();
    });
};

const detectLargestFace = async (img: HTMLImageElement): Promise<BoundingBox | null> => {
    console.log("Loading MediaPipe vision tasks");
    const vision = await FilesetResolver.forVisionTasks(process.env.NEXT_PUBLIC_BASE_PATH + "/wasm");
    console.log("Loading face detector model");
    const faceDetector = await FaceDetector.createFromModelPath(
        vision,
        process.env.NEXT_PUBLIC_BASE_PATH + "/facedetection/blaze_face_short_range.tflite",
    );
    console.log("Running face detector");
    const result = faceDetector.detect(img);
    console.log("Face Detection: ", result);
    const detections = result.detections;
    console.log("Detections lenght: ", detections.length);
    if (detections.length === 0) {
        store.updateFaceNotDetected(true);
        store.resetAll();
        store.updateRenderChatbotContent(ChatbotUiState.MASK);
        return null;
    }
    let largestFace: BoundingBox | null = null;
    let largestArea = 0;
    detections.forEach((detection) => {
        if (detection.boundingBox) {
            const { width, height } = detection.boundingBox;
            const area = width * height;
            if (area > largestArea) {
                largestArea = area;
                largestFace = detection.boundingBox;
            }
        }
    });
    return largestFace;
};

const calculatePadding = (height: number): number => {
    const x1 = 1152,
        y1 = 300;
    const x2 = 2200,
        y2 = 500;
    return y1 + ((y2 - y1) / (x2 - x1)) * (height - x1);
};

export const loadImage = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
};

const isFaceWellPositioned = (
    boundingBox: BoundingBox,
    imgWidth: number,
    imgHeight: number,
    padding: number,
): boolean => {
    const { originX, originY, width, height } = boundingBox;

    // Check if the face is far enough from the edges based on padding
    if (originX < padding || originY < padding) {
        return false;
    }

    return !(originX + width + padding > imgWidth || originY + height + padding > imgHeight);
};

export const uploadImage = async (
    blob: Blob,
    apiURL: string
): Promise<{ agedUrl: string; cutoutUrl: string }> => {
    const formData = new FormData();
    formData.append("image", blob, "image.png");

    try {
        const response = await fetch(apiURL, {
            method: "POST",
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Backend returned ${response.status}`);
        }

        const data = await response.json();


        if (!data.aged || !data.cutout) {
            throw new Error("Backend response missing expected images");
        }

        const agedUrl = URL.createObjectURL(base64ToBlob(data.aged));
        const cutoutUrl = URL.createObjectURL(base64ToBlob(data.cutout));

        store.updateImageAgedUrl(agedUrl);
        store.updateImagePresentUrl(cutoutUrl);

        return { agedUrl, cutoutUrl };
    } catch (e) {
        console.error("Error uploading image to processing API:", e);
        throw e;
    }
};

function base64ToBlob(base64: string): Blob {
    const byteString = atob(base64);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
        intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: "image/png" });
}
export async function prepareCanvasForBackend(imageSrc: string, isOnline: boolean): Promise<HTMLCanvasElement> {
    // 1. Bild laden
    const img = new Image();
    img.src = imageSrc;
    await img.decode();

    // 2. Erstes Canvas: Bild um 270° drehen
    const rotatedCanvas = document.createElement("canvas");
    rotatedCanvas.width = img.height;
    rotatedCanvas.height = img.width;

    const rctx = rotatedCanvas.getContext("2d")!;
    rctx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2);
    rctx.rotate((270 * Math.PI) / 180);
    rctx.drawImage(img, -img.width / 2, -img.height / 2);

    // Canvas → Image für die Face-Detection
    const rotatedImage = await canvasToImage(rotatedCanvas);

    // 3. FACE DETECTION AUF DEM GEDREHTEN BILD
    const largestFace = await detectLargestFace(rotatedImage);
    if (!largestFace) throw new Error("No face found after rotation.");

    const { originX, originY, width, height } = largestFace;

    // 4. Scaling wie in deiner funktionierenden Version
    const smallerSide = Math.min(rotatedCanvas.width, rotatedCanvas.height);
    const scaledWidth = 1024 * (rotatedCanvas.width / smallerSide);
    const scaledHeight = 1024 * (rotatedCanvas.height / smallerSide);
    const offsetX = (1024 - scaledWidth) / 2;
    const offsetY = (1024 - scaledHeight) / 2;

    // 5. Finales 1024×1024 Canvas bauen
    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = isOnline ? 1024 : 2160;
    finalCanvas.height = isOnline ? 1024 : 2160;

    const fctx = finalCanvas.getContext("2d")!;
    fctx.clearRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Das gedrehte Bild korrekt skalieren & platzieren
    fctx.drawImage(rotatedCanvas, offsetX, offsetY, scaledWidth, scaledHeight);

    // 6. Fertiges Canvas zurückgeben
    return finalCanvas;
}


// Hilfsfunktion: Canvas → Image
function canvasToImage(canvas: HTMLCanvasElement): Promise<HTMLImageElement> {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = canvas.toDataURL("image/png");
    });
}
